/*
 * Footer email signup — see footer-email-signup-spec.md
 *
 * Two independent, unauthenticated calls go out on submit:
 *
 *   Klaviyo  POST /client/subscriptions                       gates the message
 *   HubSpot  POST /submissions/v3/integration/submit/...      logged only
 *
 * Nothing is persisted first. On brimarstore a row was written to
 * bs_klaviyo_form_signups before any third party was called, and a cron drained
 * it every minute with an attempt counter, so an outage at either vendor did not
 * lose the signup. A Shopify storefront can reach neither that table nor that
 * cron, so this flow is stateless and a submission that fails is gone — there is
 * nothing to retry from. That is an accepted trade-off for a footer field
 * (spec §1), and the reason every failure path below writes to console.error:
 * a systematic breakage should be noticeable rather than silent.
 *
 * Anti-spam: none, deliberately. brimarstore rate-limited to 10 signups per hour
 * per IP, but that was a database count query and there is no database here. A
 * honeypot and a captcha were both considered and declined for this field; it
 * relies on the abuse controls Klaviyo and the HubSpot Forms API apply on their
 * side (spec §9). This is a knowing decision, not an omission — revisit it if
 * junk subscriptions show up in the list.
 */

if (!customElements.get("footer-email-signup")) {
  customElements.define(
    "footer-email-signup",
    class FooterEmailSignup extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.config = window.footerEmailSignup;

        if (!this.config) {
          console.error(
            "[footer-email-signup] window.footerEmailSignup is missing. Is snippets/footer-email-signup-config.liquid rendered before this script?",
          );
          return;
        }

        this.strings = this.config.strings;
        this.form = this.querySelector(".footer-email-signup-form");
        this.pair = this.querySelector(".footer-email-signup-pair");
        this.input = this.querySelector(".footer-email-signup-input");
        this.button = this.querySelector(".footer-email-signup-button");
        this.callout = this.querySelector(".footer-email-signup-callout");
        this.message = this.querySelector(".footer-email-signup-callout-message");

        this.submitting = false;
        this.successTimeout = null;

        this.onSubmit = this.onSubmit.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);

        this.form.addEventListener("submit", this.onSubmit);
        this.input.addEventListener("input", this.onInput);
      }

      disconnectedCallback() {
        this.form?.removeEventListener("submit", this.onSubmit);
        this.input?.removeEventListener("input", this.onInput);
        this.stopListeningForDismissal();
        clearTimeout(this.successTimeout);
      }

      /* --------------------------------------------------------------------
       * Validation (spec §4) — always client side, before any network request.
       * ------------------------------------------------------------------ */

      validate() {
        const trimmed = this.input.value.trim();

        // Write the trimmed value back so the constraint check, and the address
        // actually sent, are the same string the visitor will be told about.
        if (trimmed !== this.input.value) {
          this.input.value = trimmed;
        }

        if (!trimmed) {
          return this.strings.errorRequired;
        }

        // type="email" plus the pattern attribute; the pattern is what rejects
        // an address with no dot in the domain, which type="email" alone allows.
        // Read .validity rather than calling checkValidity(), which would also
        // fire an `invalid` event as a side effect.
        if (!this.input.validity.valid) {
          return this.strings.errorInvalid;
        }

        return null;
      }

      /* --------------------------------------------------------------------
       * Submit
       * ------------------------------------------------------------------ */

      async onSubmit(event) {
        event.preventDefault();

        if (this.submitting) {
          return;
        }

        const validationError = this.validate();

        if (validationError) {
          this.showError(validationError);
          this.input.focus();
          return;
        }

        const email = this.input.value;

        this.submitting = true;
        this.button.setAttribute("aria-busy", "true");

        // Both requests go out together. Only the Klaviyo result decides what
        // the visitor is told: list growth is the business goal, and requiring
        // both would mean a HubSpot outage stops people joining the mailing
        // list (spec §1).
        const subscription = this.subscribeToKlaviyo(email);

        this.createHubSpotContact(email).catch((error) => {
          console.error("[footer-email-signup] HubSpot contact not created:", error);
        });

        let subscribed = false;

        try {
          subscribed = await subscription;
        } catch (error) {
          console.error("[footer-email-signup] Klaviyo subscription failed:", error);
        } finally {
          this.submitting = false;
          this.button.removeAttribute("aria-busy");
        }

        // The message reports the actual result of the call, never merely that
        // the request was dispatched (spec §1).
        if (subscribed) {
          this.input.value = "";
          this.showSuccess();
        } else {
          this.showError(this.strings.errorGeneric, false);
        }
      }

      /* --------------------------------------------------------------------
       * Klaviyo — public, company-id keyed, no secret required.
       * ------------------------------------------------------------------ */

      async subscribeToKlaviyo(email) {
        const { companyId, listId, revision } = this.config.klaviyo;

        if (!companyId || !listId) {
          console.error(
            "[footer-email-signup] Klaviyo companyId or listId is empty in snippets/footer-email-signup-config.liquid — no subscription was attempted.",
          );
          return false;
        }

        const response = await fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/vnd.api+json",
              revision: revision,
            },
            body: JSON.stringify({
              data: {
                type: "subscription",
                attributes: {
                  custom_source: this.config.source,
                  profile: {
                    data: {
                      type: "profile",
                      attributes: {
                        email: email,
                        // Carried for attribution, so reporting lines up with
                        // the other Brimar properties (spec §1).
                        properties: { source: this.config.source },
                      },
                    },
                  },
                  // No explicit marketing consent is sent on purpose. Omitting
                  // it leaves the list's own opt-in configuration in charge, so
                  // a double opt-in list still sends its confirmation email.
                  // brimarstore bypasses opt-in entirely via the private-key
                  // profile-import route; this does not (spec §10, q4).
                },
                relationships: {
                  list: { data: { type: "list", id: listId } },
                },
              },
            }),
          },
        );

        // 202 Accepted on success.
        if (!response.ok) {
          const detail = await response.text().catch(() => "");
          console.error(
            `[footer-email-signup] Klaviyo returned ${response.status}:`,
            detail,
          );
          return false;
        }

        return true;
      }

      /* --------------------------------------------------------------------
       * HubSpot — the Forms API submission endpoint is designed to be called
       * from the browser and needs no secret. The contact is created or updated
       * keyed on email address, and attributed to the form itself.
       * ------------------------------------------------------------------ */

      async createHubSpotContact(email) {
        const { portalId, formGuid, sourcePropertyName } = this.config.hubspot;

        if (!portalId || !formGuid) {
          console.error(
            "[footer-email-signup] HubSpot portalId or formGuid is empty in snippets/footer-email-signup-config.liquid — no contact was created.",
          );
          return false;
        }

        const fields = [{ objectTypeId: "0-1", name: "email", value: email }];

        // Only sent when a property name is configured. On brimarstore the
        // source only reached HubSpot if a config row pointed at a property
        // that actually existed; an unknown property name is rejected here too.
        if (sourcePropertyName) {
          fields.push({
            objectTypeId: "0-1",
            name: sourcePropertyName,
            value: this.config.source,
          });
        }

        // Deliberately NO `hutk`. Spec §1 offers the visitor's hubspotutk
        // cookie as an option for HubSpot's own session attribution, but it
        // defeats the "upsert by email" requirement stated in the same section,
        // because it outranks the email address in HubSpot's contact matching:
        //
        //   same email + same hutk       -> updates that contact
        //   same email + different hutk  -> updates that contact (by email)
        //   different email + same hutk  -> UPDATES THAT CONTACT'S EMAIL
        //
        // That last row is the problem. Any visitor whose browser carries a
        // hubspotutk — anyone who has ever hit a HubSpot-tracked CCW page, and
        // the cookie outlives the tracking script that set it — would have
        // their footer signup silently overwrite the email of whatever contact
        // the cookie maps to, rather than creating a new one. On a shared or
        // kiosk browser that corrupts real records. Observed in testing: three
        // signups with different addresses collapsed onto one contact.
        //
        // pageUri still gives the useful half of the attribution.
        const context = {
          pageUri: window.location.href,
          pageName: document.title,
        };

        const response = await fetch(
          `https://api.hsforms.com/submissions/v3/integration/submit/${encodeURIComponent(portalId)}/${encodeURIComponent(formGuid)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ fields: fields, context: context }),
          },
        );

        if (!response.ok) {
          const detail = await response.text().catch(() => "");
          console.error(
            `[footer-email-signup] HubSpot returned ${response.status}:`,
            detail,
          );
          return false;
        }

        return true;
      }

      /* --------------------------------------------------------------------
       * The callout (spec §5, §6) — one slot, both states.
       * ------------------------------------------------------------------ */

      /*
       * markFieldInvalid distinguishes the two kinds of error that share this
       * slot. A validation failure is the field's fault, so the pair gets the
       * red border and the input is marked aria-invalid. A failed request is
       * not: the address may be perfectly good and the visitor has nothing to
       * correct, so it gets the red bubble alone. Marking a valid field invalid
       * would tell a screen reader user to fix an input that is already right.
       */
      showError(template, markFieldInvalid = true) {
        this.setCallout("is-error", template);

        if (markFieldInvalid) {
          this.pair.classList.add("has-error");
          this.input.setAttribute("aria-invalid", "true");
        }
      }

      showSuccess() {
        // setCallout() clears any timer left over from a previous message, so
        // the 5s window always starts fresh here (spec §6).
        this.setCallout("is-success", this.strings.success);

        // Under reduced motion the message is left up rather than taken away on
        // a timer; it is still dismissible by clicking anywhere (spec §8).
        if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          this.successTimeout = setTimeout(() => this.hideCallout(), 5000);
        }
      }

      setCallout(state, template) {
        // Any pending auto-dismiss belongs to the message being replaced here.
        // Left running, it would fire against whatever is on screen when it
        // expires and cut a later alert short — a success at t=0 followed by an
        // error at t=2s would see the error vanish 3s later. That is the stale
        // timer spec §6 warns about, and clearing it on every state change
        // rather than only on hide is what rules it out.
        clearTimeout(this.successTimeout);
        this.successTimeout = null;

        this.callout.classList.remove("is-error", "is-success");
        this.callout.classList.add(state);

        this.message.replaceChildren(this.buildMessage(template));

        this.listenForDismissal();
      }

      hideCallout() {
        this.callout.classList.remove("is-error", "is-success");
        this.message.replaceChildren();

        this.pair.classList.remove("has-error");
        this.input.removeAttribute("aria-invalid");

        this.stopListeningForDismissal();

        clearTimeout(this.successTimeout);
        this.successTimeout = null;
      }

      listenForDismissal() {
        document.addEventListener("click", this.onDocumentClick);

        // WebKit does not dispatch a click to document for taps on
        // non-interactive elements, so on iOS the click listener alone would
        // never fire and "dismisses on a click or tap anywhere" would hold on
        // desktop only.
        document.addEventListener("touchstart", this.onDocumentClick, {
          passive: true,
        });
      }

      stopListeningForDismissal() {
        document.removeEventListener("click", this.onDocumentClick);
        document.removeEventListener("touchstart", this.onDocumentClick, {
          passive: true,
        });
      }

      /*
       * Builds "<strong>Email Address</strong> is required." from the localised
       * "[field] is required." without going through innerHTML. The bold part is
       * the field's visible label, so the two stay in step if the label changes.
       */
      buildMessage(template) {
        const fragment = document.createDocumentFragment();
        const parts = String(template).split("[field]");

        parts.forEach((part, index) => {
          if (index > 0) {
            const strong = document.createElement("strong");
            strong.textContent = this.strings.fieldLabel;
            fragment.appendChild(strong);
          }

          if (part) {
            fragment.appendChild(document.createTextNode(part));
          }
        });

        return fragment;
      }

      /* --------------------------------------------------------------------
       * Dismissal (spec §6)
       * ------------------------------------------------------------------ */

      onInput() {
        // The error clears as soon as the visitor edits the field. Keyed on the
        // callout's own state rather than the pair's, so a failed-request error
        // — which never marks the pair — clears on edit too. A success message
        // is left alone; it has its own timer.
        if (this.callout.classList.contains("is-error")) {
          this.hideCallout();
        }
      }

      onDocumentClick(event) {
        // Either alert dismisses on a click or tap anywhere, with one mandatory
        // exception: the Submit button (spec §6).
        //
        // The spec describes this as the raising click bubbling to document
        // after the submit handler has displayed the alert. The ordering here is
        // actually the reverse — the click finishes propagating to document
        // first, and only then does the button's activation behaviour fire the
        // submit event — so the click that raises an alert cannot reach a
        // listener registered during that same submit.
        //
        // The guard is still load-bearing, for the click after: pressing Submit
        // again while an alert is up would otherwise dismiss it on the way down
        // and re-raise it in the same gesture. preventDefault() on the submit
        // does not stop click propagation, so it has to be an explicit check
        // either way.
        if (this.button.contains(event.target)) {
          return;
        }

        this.hideCallout();
      }
    },
  );
}
